import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import AppTour from './components/AppTour';
import AccessibilityPanel from './components/AccessibilityPanel';
import ErrorBoundary from './components/ErrorBoundary';
import LoginModal from './components/LoginModal';
import CookieConsent from './components/CookieConsent';
import usePageviewTracker from './hooks/usePageviewTracker';

// Pages — Home eager (LCP-kritische landingsroute), de rest lazy-loaded via
// route-based code-splitting zodat zware afhankelijkheden (Leaflet-kaarten,
// Recharts-dashboards, editors) NIET in de initiële bundle zitten. Dit
// verlaagt de hoeveelheid ongebruikte JS op de homepage fors (Lighthouse).
import Home from './pages/Home';
const Welkom = lazy(() => import('./pages/Welkom'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Discover = lazy(() => import('./pages/Discover'));
const CreateActivity = lazy(() => import('./pages/CreateActivity'));
const EditActivity = lazy(() => import('./pages/EditActivity'));
const ActivityDetail = lazy(() => import('./pages/ActivityDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Admin = lazy(() => import('./pages/Admin'));
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'));
const Settings = lazy(() => import('./pages/Settings'));
const News = lazy(() => import('./pages/News'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const TestimonialDetail = lazy(() => import('./pages/TestimonialDetail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ClaimInvitation = lazy(() => import('./pages/ClaimInvitation'));
const Help = lazy(() => import('./pages/Help'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Contact = lazy(() => import('./pages/Contact'));
const FreshnessResponse = lazy(() => import('./pages/FreshnessResponse'));
const SEOLandingPage = lazy(() => import('./pages/SEOLandingPage'));
const PrivacyStatement = lazy(() => import('./pages/PrivacyStatement'));
const CookieStatement = lazy(() => import('./pages/CookieStatement'));
const PosterGenerator = lazy(() => import('./pages/PosterGenerator'));

// Lichtgewicht fallback tijdens het laden van een lazy route-chunk.
const PageLoader = () => (
  <div className="flex justify-center items-center py-24" data-testid="route-loader">
    <div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <AccessibilityProvider>
      <AuthProvider>
        <TranslationProvider>
        <BrowserRouter>
          <Layout>
            <AppTour />
            <PageviewTracker />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/welkom" element={<Welkom />} />
              <Route path="/login" element={<Login />} />
              <Route path="/wachtwoord-vergeten" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/uitnodiging" element={<ClaimInvitation />} />
              <Route path="/register" element={<Register />} />
              {/* /discover blijft bestaan voor diepe links, maar redirect naar / */}
              <Route path="/discover" element={<Navigate to="/" replace />} />
              {/* Buren als directe nav-bestemming — hergebruikt de Buren-tab in Discover */}
              <Route path="/buren" element={<Discover />} />
              <Route path="/activiteit-toevoegen" element={<CreateActivity />} />
              {/* Backwards compat: oude /create URL redirect naar nieuwe SEO-vriendelijke route */}
              <Route path="/create" element={<Navigate to="/activiteit-toevoegen" replace />} />
              <Route path="/edit/:id" element={<EditActivity />} />
              <Route path="/activity/:id" element={<ActivityDetail />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/:id" element={<Profile />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/superadmin" element={<SuperAdmin />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/verhalen" element={<News />} />
              <Route path="/verhalen/:postId" element={<NewsDetail />} />
              {/* Backwards compat: old /inspiratie + /nieuws URLs redirect to /verhalen */}
              <Route path="/inspiratie" element={<Navigate to="/verhalen" replace />} />
              <Route path="/inspiratie/:postId" element={<RedirectVerhalen />} />
              <Route path="/nieuws" element={<Navigate to="/verhalen" replace />} />
              <Route path="/nieuws/:postId" element={<RedirectVerhalen />} />
              {/* Doelgroep-deeplinks (campagne) — opent home met preset filter */}
              <Route path="/jongeren" element={<SEOLandingPage />} />
              <Route path="/senioren" element={<Home preset="senioren" />} />
              <Route path="/volwassenen" element={<SEOLandingPage />} />
              <Route path="/kinderen" element={<Home preset="kinderen" />} />
              <Route path="/jongeren-volwassenen" element={<Home preset="jongeren-volwassenen" />} />
              <Route path="/volwassenen-senioren" element={<Home preset="volwassenen-senioren" />} />
              <Route path="/gratis" element={<Home preset="gratis" />} />
              {/* Kaart-deeplinks (aug 2026) — deelbare URL naar de kaart-modus van Home. */}
              <Route path="/kaart" element={<Home preset="map" />} />
              <Route path="/map" element={<Navigate to="/kaart" replace />} />
              <Route path="/verhaal/:id" element={<TestimonialDetail />} />
              <Route path="/meldingen" element={<Notifications />} />
              <Route path="/hulp" element={<Help />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/poster" element={<PosterGenerator />} />
              <Route path="/actief-check/:checkId" element={<FreshnessResponse />} />
              {/* SEO landingspagina's per wijk/regio — config in landingPageConfigs.js. */}
              <Route path="/activiteiten-almere" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-haven" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-stad" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-buiten" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-poort" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-hout" element={<SEOLandingPage />} />
              <Route path="/activiteiten-almere-oosterwold" element={<SEOLandingPage />} />
              {/* Wijk-/stadsdeel-pagina's (stad/wijk-zoekintentie, sep 2026) */}
              <Route path="/almere-haven" element={<SEOLandingPage />} />
              <Route path="/almere-stad" element={<SEOLandingPage />} />
              <Route path="/almere-poort" element={<SEOLandingPage />} />
              <Route path="/almere-hout" element={<SEOLandingPage />} />
              <Route path="/wat-te-doen-in-almere" element={<SEOLandingPage />} />
              <Route path="/sporten-almere" element={<SEOLandingPage />} />
              <Route path="/wandelen-almere" element={<SEOLandingPage />} />
              {/* Fase 2 (feb 2026) — 16 nieuwe thema-landingspagina's (Julian). */}
              <Route path="/evenementen-almere" element={<SEOLandingPage />} />
              <Route path="/buurthuis-almere" element={<SEOLandingPage />} />
              <Route path="/workshops-almere" element={<SEOLandingPage />} />
              <Route path="/creatieve-workshops-almere" element={<SEOLandingPage />} />
              <Route path="/dansen-almere" element={<SEOLandingPage />} />
              <Route path="/zumba-almere" element={<SEOLandingPage />} />
              <Route path="/muziek-almere" element={<SEOLandingPage />} />
              <Route path="/zangles-almere" element={<SEOLandingPage />} />
              <Route path="/padel-almere" element={<SEOLandingPage />} />
              <Route path="/yoga-almere" element={<SEOLandingPage />} />
              <Route path="/karate-almere" element={<SEOLandingPage />} />
              <Route path="/voetbal-almere" element={<SEOLandingPage />} />
              <Route path="/kickboksen-almere" element={<SEOLandingPage />} />
              <Route path="/basketbal-almere" element={<SEOLandingPage />} />
              <Route path="/volleybal-almere" element={<SEOLandingPage />} />
              <Route path="/badminton-almere" element={<SEOLandingPage />} />
              {/* Fase 3 (sep 2026) — 4 nieuwe thema-landingspagina's (Julian). */}
              <Route path="/activiteiten-ouderen-almere" element={<SEOLandingPage />} />
              <Route path="/gratis-activiteiten-almere" element={<SEOLandingPage />} />
              <Route path="/creatieve-activiteiten-almere" element={<SEOLandingPage />} />
              <Route path="/ontmoetingsactiviteiten-almere" element={<SEOLandingPage />} />
              {/* Doelgroep- & categorie-landingspagina's (Margo, okt 2026). /jongeren en /volwassenen hierboven. */}
              <Route path="/eten-en-koken" element={<SEOLandingPage />} />

              {/* Categorie + doelgroep campagne-landingspagina's (Loes/PGA socials, okt 2026) */}
              <Route path="/muziek-jongeren-almere" element={<SEOLandingPage />} />
              <Route path="/sociaal-jongeren-almere" element={<SEOLandingPage />} />
              <Route path="/sport-volwassenen-almere" element={<SEOLandingPage />} />
              <Route path="/natuur-volwassenen-almere" element={<SEOLandingPage />} />
              {/* Publieke juridische pagina's */}
              <Route path="/privacyverklaring" element={<PrivacyStatement />} />
              <Route path="/privacy" element={<PrivacyStatement />} />
              <Route path="/cookies" element={<CookieStatement />} />
            </Routes>
            </Suspense>
          </Layout>
          <GlobalLoginModal />
        </BrowserRouter>
        </TranslationProvider>
        <AccessibilityPanel />
        <CookieConsent />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </AccessibilityProvider>
    </ErrorBoundary>
  );
}

// Global login modal that reads from AuthContext
function GlobalLoginModal() {
  const { loginModalOpen, setLoginModalOpen, loginModalMessage, handleLoginModalSuccess } = useAuth();
  return (
    <LoginModal
      open={loginModalOpen}
      onOpenChange={setLoginModalOpen}
      message={loginModalMessage}
      onSuccess={handleLoginModalSuccess}
    />
  );
}

// Pageview tracker — must live inside BrowserRouter to access useLocation.
function PageviewTracker() {
  usePageviewTracker();
  return null;
}

// Redirect old /inspiratie/:postId and /nieuws/:postId → /verhalen/:postId
function RedirectVerhalen() {
  const { postId } = useParams();
  return <Navigate to={`/verhalen/${postId}`} replace />;
}

export default App;
